<template>
  <loadingComponent ref="loadingComponent" :loading-text="$t('loading')">
    <div class="filterable">
      <template v-if="showOrderAndFilter">
        <div
          class="card border-secondary"
        >
          <div class="card-header text-dark">
            <div>
              <span>{{ $t('match') }}</span>
              <select v-model="query.filter_match">
                <option value="and">{{ $t('and') }}</option>
                <option value="or">{{ $t('or') }}</option>
              </select>
              <span>{{ $t('order_by') }}:</span>
              <select
                :disabled="loading"
                @input="updateOrderColumn"
              >
                <option
                  v-for="(column, orderIndex) in orderables"
                  :key="`update-order-${orderIndex}`"
                  :value="column.name"
                  :selected="column && column.name == query.order_column"
                >{{column.title}}</option>
              </select>
              <template v-if="query.order_column !== ''">
                <strong @click="updateOrderDirection" class="badge badge-light">
                  <span v-if="query.order_direction === 'asc'">&uarr;</span>
                  <span v-else>&darr;</span>
                </strong>
                <span>{{ $t('of_the_following') }}:</span>
              </template>
            </div>
          </div>
          <div class="card-body">
            <div class="filter">
              <div
                class="row bg-light mb-3 pt-3"
                style="border: 1px solid silver"
                v-for="(f, i) in filterCandidates"
                :key="`candidate-${i}`"
              >
                <div class="filter-column col">
                  <div class="form-group">

                    <multiselect
                      v-model="f.column"
                      :options="filterGroupsOptions"
                      track-by="name"
                      label="title"
                      group-values="filters"
                      group-label="name"
                      :allow-empty="false"
                      :placeholder="$t('select_a_filter')"
                      :show-labels="false"
                      @input="selectColumn(i)"
                    >
                      <span class="multiselectOptionLabel" slot="option" slot-scope="scope">
                        <template v-if="scope.option && scope.option.$groupLabel && scope.option.$isLabel">
                          <span class="text-dark font-weight-bold text-uppercase">{{ scope.option.$groupLabel }}</span>
                        </template>
                        <template v-else>
                          <span class="ml-3">{{ scope.option.title }}</span>
                          <template v-if="scope.option.name === f.column.name">
                            <icon name="check" size="fa-1x"></icon>
                          </template>
                        </template>
                      </span>
                      <span slot="noResult">{{ $t('no_result') }}</span>
                      <span slot="noOptions">{{ $t('no_options') }}</span>
                    </multiselect>

                  </div>
                </div>
                <div class="filter-operator col" v-if="f.column">
                  <div class="form-group">
                    <select class="form-control" @input="selectOperator(f, i, $event)">
                      <option
                        v-for="(operator, operatorIndex) in fetchOperators(f)"
                        :key="`filter-operator-${operatorIndex}`"
                        :value="JSON.stringify(operator)"
                        :selected="f.operator && operator.name === f.operator.name"
                      >{{operator.title}}</option>
                    </select>
                  </div>
                </div>
                <template v-if="f.column && f.operator">
                  <div class="filter-full col-md-4" v-if="f.operator.component === 'single'">
                    <input type="text" class="form-control" v-model="f.query_1">
                  </div>
                  <template v-if="f.operator.component === 'double'">
                    <div class="filter-query_1 col-md-2">
                      <input type="text" class="form-control mb-2" v-model="f.query_1">
                    </div>
                    <div class="filter-query_2 col-md-2">
                      <input type="text" class="form-control mb-2" v-model="f.query_2">
                    </div>
                  </template>
                  <template v-if="f.operator.component === 'datetime_1'">
                    <div class="filter-query_1 col-md-2">
                      <input type="text" class="form-control mb-2" v-model="f.query_1">
                    </div>
                    <div class="filter-query_2 col-md-2">
                      <select class="form-control mb-2" v-model="f.query_2">
                        <option>{{ $t('hours') }}</option>
                        <option>{{ $t('days') }}</option>
                        <option>{{ $t('months') }}</option>
                        <option>{{ $t('years') }}</option>
                      </select>
                    </div>
                  </template>
                  <template v-if="f.operator.component === 'datetime_2'">
                    <div class="filter-query_2 col-md-4">
                      <select class="form-control" v-model="f.query_1">
                        <option value="yesterday">{{ $t('yesterday') }}</option>
                        <option value="today">{{ $t('today') }}</option>
                        <option value="tomorrow">{{ $t('tomorrow') }}</option>
                        <option value="last_month">{{ $t('last_month') }}</option>
                        <option value="this_month">{{ $t('this_month') }}</option>
                        <option value="next_month">{{ $t('next_month') }}</option>
                        <option value="last_year">{{ $t('last_year') }}</option>
                        <option value="this_year">{{ $t('this_year') }}</option>
                        <option value="next_year">{{ $t('next_year') }}</option>
                      </select>
                    </div>
                  </template>
                  <template v-if="f.operator.component === 'list'">
                    <div class="filter-query_1 col-md-4">
                      <multiselect
                        v-model="f.query_1"
                        :options="optionsList(f.column)"
                        track-by="value"
                        label="text"
                        :allow-empty="false"
                        :placeholder="optionsList(f.column).length > 0 ? $t('to_select') : ''"
                        :show-labels="false"
                      >
                        <span class="multiselectOptionLabel" slot="option" slot-scope="scope">
                          {{ scope.option.text }}
                          <template v-if="f.query_1 && scope.option.value === f.query_1.value">
                            <icon  name="check" size="fa-1x"></icon>
                          </template>
                        </span>
                        <span slot="noResult">{{ $t('no_result') }}</span>
                        <span slot="noOptions">{{ $t('no_options') }}</span>
                      </multiselect>
                    </div>
                  </template>
                  <template v-if="f.operator.component === 'datepicker_1'">
                    <div class="filter-query_1 col-md-4">
                      <b-form-datepicker
                        v-model="f.query_1"
                        :locale="locale"
                        :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
                        class="mb-2"
                      ></b-form-datepicker>
                    </div>
                  </template>
                  <template v-if="f.operator.component === 'datepicker_2'">
                    <div class="filter-query_1 col-md-2">
                      <b-form-datepicker
                        v-model="f.query_1"
                        :locale="locale"
                        :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
                        class="mb-2"
                      ></b-form-datepicker>
                    </div>
                    <div class="filter-query_2 col-md-2">
                      <b-form-datepicker
                        v-model="f.query_2"
                        :locale="locale"
                        :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
                        class="mb-2"
                      ></b-form-datepicker>
                    </div>
                  </template>
                </template>
                <div class="filter-remove" v-if="f">
                  <button class="btn btn-outline-danger btn-sm" @click="removeFilter(f, i)">
                    {{ $t('remove_filter') }}
                  </button>
                </div>
              </div>
              <div class="wl-container">
                <div class="btn-group btn-group-toggle">
                  <button class="btn btn-outline-dark btn-sm" @click="addFilter">+</button>
                  <button
                    class="btn btn-outline-secondary btn-sm"
                    @click="resetFilter"
                    v-if="this.appliedFilters.length > 0"
                  >{{ $t('reset_filters') }}</button>
                  <button class="btn btn-outline-primary btn-sm" @click="applyFilter">{{ $t('filter') }}</button>
                  <template v-if="determinesInspectionAction(['exportToExcel']) && checkVariable(urlExportExcel)">
                    <a
                      :href="getUrlExportFilter"
                      class="btn btn-outline-secondary btn-sm"
                      target="_blank"
                    >
                      <icon name="file-export" size="fa-1x"></icon>
                      {{ $t('to_export') }}
                    </a>
                  </template>
                  <template v-if="determinesInspectionAction(['store'])">
                    <button
                      @click="storeFilter()"
                      class="btn btn-outline-secondary btn-sm"
                      v-if="withStoreFilterAction === true"
                    >
                      <icon name="save" size="fa-1x"></icon>
                      {{ $t('save') }}
                    </button>
                  </template>
                  <template v-if="globalResourceManipulation">
                    <slot
                      name="global-actions-request"
                      :urlStatusUpdate="urlStatusUpdate"
                      :collection="collection"
                      :idMarked="idMarked"
                    ></slot>
                  </template>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
      <!-- Header -->
      <div class="table-responsive">
        <template v-if="determinesInspectionAction(['seeGlobalSums'])">
          <table class="excel-table-2007 fixed-width-table table-hover tablaVertical">
            <slot name="thead-header"></slot>
            <tbody v-if="eventFireGlobalTotal && !loadingEventFireWithGlobalTotal">
              <slot name="body-tr" :event="eventFireGlobalTotal"></slot>
            </tbody>
            <tbody v-else>
              <slot name="body-tr-empty" :event="null"></slot>
            </tbody>
          </table>
        </template>
      </div>

      <div class="table-responsive">
        <table class="excel-table-2007 fixed-width-table table-hover">
          <tbody>
            <slot
              :data="collection && collection.data && collection.data.length > 0 ? collection.data : []"
              :toRedirect="redirect"
              :idMarked="idMarked"
            ></slot>
          </tbody>
        </table>
      </div>

      <div class="wl-container mt-3">
        <select v-model="query.limit" :disabled="loading" class="mr-1" @change="updateLimit">
          <option>10</option>
          <option>15</option>
          <option>25</option>
          <option>50</option>
        </select>
        <template v-if="collection && collection.from && collection.to">
          <small>{{ $t('showing')}} {{collection.from}} - {{collection.to}} {{ $t('of') }} {{collection.total}} {{ $t('entries') }}.</small>
        </template>
        <template v-else>
          <small>{{ $t('showing')}} 0 - 0 {{ $t('of') }} 0 {{ $t('entries') }}.</small>
        </template>
      </div>
      <div class="btn-group btn-group-toggle mt-1">
        <template v-if="collection">
          <button
            class="btn btn-outline-dark btn-sm"
            :disabled="!collection.prev_page_url || loading"
            @click="prevPage"
          >&laquo; {{ $t('prev') }}</button>
          <button
            class="btn btn-outline-dark btn-sm"
            :disabled="!collection.next_page_url || loading"
            @click="nextPage"
          >{{ $t('next') }} &raquo;</button>
        </template>
      </div>
    </div>
  </loadingComponent>
</template>
<script>
import axios from 'axios'
import Vue from 'vue'

import is from '../web/plugins/helper/is'
import { getGetters } from '../plugins/mapGetter'
import ComponentLoading from '../components/Componentloading'
import IncludeHelperComponent from '../plugins/includeHelperComponent'

export default {
  name: 'v-weirdo-filterable-vertical',
  extends: IncludeHelperComponent,
  components: {
    loadingComponent: ComponentLoading,
  },
  props: {
    url: String,
    modulo_id: {
      type: Number,
      default: null,
    },
    filterGroups: Array,
    orderables: Array,
    orderColumn: {
      type: String,
      default: () => {
        return 'created_at'
      },
    },
    orderDirection: {
      type: String,
      default: () => {
        return 'desc'
      },
    },
    withGlobalTotals: {
      type: Boolean,
      default: false,
    },
    urlPusherTotals: {
      type: String,
      default: () => {
        return null
      },
    },
    urlStatusUpdate: {
      type: String,
      default: () => {
        return null
      },
    },
    globalResourceManipulation: {
      type: Boolean,
      default: false,
    },
    withAccordion: {
      type: Boolean,
      default: false,
    },
    withStoreFilterAction: {
      type: Boolean,
      default: true,
    },
    mandatoryFilter: {
      type: String,
      default: () => {
        return null
      },
    },
    urlExportExcel: {
      type: String,
      default: () => {
        return ''
      },
    },
    showOrderAndFilter: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      idMarked: [],
      loading: true,
      appliedFilters: [],
      filterCandidates: [],
      listInformation: [],
      query: {
        order_column: 'created_at',
        order_direction: 'desc',
        filter_match: 'and',
        limit: 10,
        page: 1,
      },
      collection: {
        data: [],
      },
      selected: {},
      selectedFilter: {},
    }
  },
  computed: {
    fetchOperators() {
      return f => {
        /** @var Boolean|Undefined */
        const { column } = f
        /** @var Boolean */
        const withoutContains = this.checkVariable(column) && !this.isUndefined(column.withoutContains) ? column.withoutContains : false
        /** @var Array */
        const operators =  this.availableOperators().filter(operator => {
          if (column && operator.parent.includes(column.type) && operator.name === 'contains' && withoutContains === true) {
            return false
          }

          if (column && operator.parent.includes(column.type)) {
            return operator
          }
        })

        return operators
      }
    },
    ...getGetters(),
    locale: function () {
      return is.getValueSetting('locale')
    },
    optionsList() {
      return column => {
        return this.getListInformation(column)
      }
    },
    getUrlExportFilter() {
      return this.exportToCSV()
    },
    filterGroupsOptions() {
      let filterGroupsList = []

      if (this.filterGroups) {
        for (const groupKey in this.filterGroups) {
          const group = this.filterGroups[groupKey]
          const { filters } = group
          let filtersList = []

          for (const filterKey in filters) {
            const filter = filters[filterKey]

            if (this.checkIndicatedPermission(filter) === true) {
              const filterObject = {
                ...filter,
                $isDisabled: this.checkFilterCandidates(filter) === false,
              }

              filtersList.push(filterObject)
            }
          }

          const groupObject = {
            name: group.name,
            filters: filtersList,
          }

          filterGroupsList.push(groupObject)
        }
      }

      return filterGroupsList
    },
  },
  watch: {
    async selected(selected) {
      if (selected && selected.action) {
        await this.$store.dispatch(selected.action, this.checkVariable(selected.params) ? selected.params : null)
      }
    },
    'delete_model': function () {
      this.applyChange()
    },
    'updated_model': function (t) {
      this.applyChange()
    },
    'filterCandidates': function (filterCandidates) {
      if (this.checkVariable(filterCandidates) && this.isArray(filterCandidates) && filterCandidates.length > 0) {
        filterCandidates.forEach(candidate => {
          const { column } = candidate

          if (this.checkVariable(column, column.key)) {
            const temp = this[column.key]
            if (this.checkVariable(temp) && this.isArray(temp) && temp.length === 0) {
              this.$store.dispatch(column.action, this.checkVariable(column.params) ? column.params : null)
            }
          }
        })
      }
    },
    appliedFilters: function (appliedFilters) {
      const filters = this.getFilters()
      const params = {
        ...filters,
        ...this.query,
      }

      if (this.checkVariable(this.Echo) && this.withGlobalTotals && this.determinesInspectionAction(['withGlobalTotalUpdate'])) {
        this.$store.dispatch(
          'setEventFireWithGlobalTotal',
          {
            echo: this.Echo,
            params: {
              ...params,
              with_global_totals: this.withGlobalTotals,
            },
            url: this.urlPusherTotals,
          },
        )
      }
    },
    'eventStatus': function () {
      this.applyChange()
    },
  },
  mounted: function () {
    this.$nextTick(function () {
      if (this.checkVariable(this.urlPusherTotals)) {
        this.Echo = this.connectionToPusherJs()
      }

      if (this.checkVariable(this.orderColumn)) {
        Vue.set(this.query, 'order_column', this.orderColumn)
      }

      if (this.checkVariable(this.orderDirection)) {
        Vue.set(this.query, 'order_direction', this.orderDirection)
      }

      const query = localStorage.getItem(this.replaceAll(`filter-index-${this.url}-query`, '/', ''))
      if (query) {
        Vue.set(
          this.$data,
          'query',
          JSON.parse(query),
        )
      }

      const filterCandidates = localStorage.getItem(this.replaceAll(`filter-index-${this.url}-filterCandidates`, '/', ''))
      if (filterCandidates) {
        Vue.set(
          this.$data,
          'filterCandidates',
          JSON.parse(filterCandidates),
        )
        Vue.set(
          this.$data,
          'appliedFilters',
          JSON.parse(filterCandidates),
        )
      } else {
        this.addFilter()
      }

      this.fetch()
    })
  },
  methods: {
    checkIndicatedPermission(f) {
      if (this.checkVariable(f) && this.checkVariable(f.permits)) {
        return this.determinesInspectionAction(this.isArray(f.permits) ? f.permits : [f.permits])
      }

      return true
    },
    checkFilterCandidates(option) {
      if (this.checkVariable(option.singleMandatoryFilter) && option.singleMandatoryFilter === false) {
        return true
      }
      if (!this.checkVariable(option.singleMandatoryFilter)) {
        return true
      }

      const result = this.findWith(this.filterCandidates, val => {
        return this.checkVariable(val.column)
      })

      return result === -1 ? true : false
    },
    redirect(route) {
      if (this.checkVariable(route)) {
        if (this.checkVariable(this.url) && this.checkVariable(route.name)) {
          this.updateCachePaginationDetail(route.name)
        }

        this.pushRoute(route)
      }
    },
    getListInformation(column) {
      if (this.checkVariable(column) && this.checkVariable(column.key) && !this.checkVariable(column.options)) {
        const temp = this[column.key]

        return (temp && temp.length > 0) ? temp : []
      } else if (this.checkVariable(column) && !this.checkVariable(column.key) && this.checkVariable(column.options)) {
        return (column.options) ? column.options : []
      }

      return []
    },
    updateOrderDirection() {
      if (this.query.order_direction === 'desc') {
        this.query.order_direction = 'asc'
      } else {
        this.query.order_direction = 'desc'
      }
      this.updateCache()
      this.applyChange()
    },
    updateOrderColumn(e) {
      const value = e.target.value
      Vue.set(this.query, 'order_column', value)
      this.updateCache()
      this.applyChange()
    },
    resetFilter() {
      Vue.set(this.$data, 'idMarked', [])
      this.appliedFilters.splice(0)
      this.filterCandidates.splice(0)
      this.addFilter()
      this.query.page = 1
      this.updateCache()
      this.applyChange()
    },
    applyFilter() {
      Vue.set(this.$data, 'idMarked', [])
      Vue.set(
        this.$data,
        'appliedFilters',
        JSON.parse(JSON.stringify(this.filterCandidates)),
      )
      this.query.page = 1
      this.updateCache()
      this.applyChange()
    },
    removeFilter(f, i) {
      this.deleteBrowserCacheQuery()
      this.deleteBrowserCacheFilterCandidates()
      this.filterCandidates.splice(i, 1)
    },
    replaceSearchList(f) {
      if (this.checkVariable(f.column) && this.checkVariable(f.column.replace) && this.isArray(f.column.replace)) {
        const result = this.whereInWith(f.column.replace, { operator: f.operator.name })
        const first = this.getTheFirst(result)
        if (this.checkVariable(first) && this.checkVariable(first.name)) {
          this.$set(f.column, 'name', first.name)
        }
      }

      return f
    },
    replaceListObject(f) {
      if (f.operator.component === 'list') {
        if (this.isObject(f.query_1)) {
          this.$set(f, 'query_1', this.getObjectValue(f.query_1, 'value'))
        }
        if (this.isObject(f.query_2)) {
          this.$set(f, 'query_2', this.getObjectValue(f.query_2, 'value'))
        }
      }

      return f
    },
    selectOperator(f, i, e) {
      const value = e.target.value
      if (value.length === 0) {
        Vue.set(this.filterCandidates[i], 'operator', value)
        return
      }

      const obj = JSON.parse(value)
      Vue.set(this.filterCandidates[i], 'operator', obj)

      this.filterCandidates[i].query_1 = null
      this.filterCandidates[i].query_2 = null

      switch (obj.name) {
        case 'in_the_past':
        case 'in_the_next':
          this.filterCandidates[i].query_1 = 28
          this.filterCandidates[i].query_2 = 'days'
          break
        case 'in_the_peroid':
          this.filterCandidates[i].query_1 = 'today'
          break
      }
    },
    selectColumn(i) {
      const obj = this.filterCandidates[i].column
      this.selected = obj

      // set default operator: todo
      switch (obj.type) {
        case 'numeric':
          this.filterCandidates[i].operator = this.availableOperators()[0]
          this.filterCandidates[i].query_1 = null
          this.filterCandidates[i].query_2 = null
          break
        case 'string':
          this.filterCandidates[i].operator = this.availableOperators()[7]
          this.filterCandidates[i].query_1 = null
          this.filterCandidates[i].query_2 = null
          break
        case 'datetime':
          this.filterCandidates[i].operator = this.availableOperators()[10]
          this.filterCandidates[i].query_1 = 28
          this.filterCandidates[i].query_2 = 'days'
          break
        case 'counter':
          this.filterCandidates[i].operator = this.availableOperators()[14]
          this.filterCandidates[i].query_1 = null
          this.filterCandidates[i].query_2 = null
          break
        case 'select':
          this.filterCandidates[i].operator = this.availableOperators()[17]
          this.filterCandidates[i].query_1 = null
          this.filterCandidates[i].query_2 = null
          break
        case 'datepicker':
          this.filterCandidates[i].operator = this.availableOperators()[19]
          this.filterCandidates[i].query_1 = null
          this.filterCandidates[i].query_2 = null
          break
      }
    },
    addFilter() {
      this.filterCandidates.push({
        column: '',
        operator: '',
        query_1: null,
        query_2: null,
      })
    },
    applyChange() {
      this.fetch()
    },
    updateLimit() {
      this.query.page = 1
      this.updateCacheQuery()
      this.applyChange()
    },
    prevPage() {
      if (this.collection.prev_page_url) {
        this.query.page = Number(this.query.page) - 1
        this.updateCacheQuery()
        this.applyChange()
      }
    },
    nextPage() {
      if (this.collection.next_page_url) {
        this.query.page = Number(this.query.page) + 1
        this.updateCacheQuery()
        this.applyChange()
      }
    },
    getFilters() {
      const f = {}
      this.appliedFilters.forEach((filter, i) => {
        filter = this.replaceSearchList(filter)
        filter = this.replaceListObject(filter)
        if (filter && filter.column && filter.column.type === 'datepicker') {
          const { withBetween } = filter.column
          let format = 'YYYY-MM-DD'
          if (this.checkVariable(withBetween) && this.isBooleand(withBetween) === true) {
            format = 'YYYY-MM-DD HH:mm:ss'
            if (!this.checkVariable(filter.query_2) && ['equal_to', 'not_equal_to'].indexOf(filter.operator.name) !== -1) {
              this.$set(filter, 'query_2', filter.query_1)
              this.$set(filter.operator, 'name', 'between')
            }
          }
          if (filter.query_1) {
            this.$set(filter, 'query_1', this.startOfHour(filter.query_1, format).format(format))
          }

          if (filter.query_2) {
            this.$set(filter, 'query_2', this.endOfHour(filter.query_2, format).format(format))
          }
        }

        f[`f[${i}][column]`] = filter.column.name
        f[`f[${i}][operator]`] = filter.operator.name
        f[`f[${i}][query_1]`] = filter.query_1
        f[`f[${i}][query_2]`] = filter.query_2
      })

      return f
    },
    createFilter() {
      if (this.checkVariable(this.mandatoryFilter)) {
        const result = this.findWith(this.appliedFilters, (ob) => {
          return ob.column.name === this.mandatoryFilter
        })
        if (result === -1) {
          this.swalIcon = 'warning'
          this.swalText = `${this.$t('required_mandatory_filter')} ${this.mandatoryFilter}`
          this.defaultSettingsSwal()
          this.positionedDialogToast()

          return false
        }
      }

      const filters = this.getFilters()

      return {
        ...filters,
        ...this.query,
      }
    },
    fetch() {
      const self = this
      this.loading = true
      const params = this.createFilter()
      if (params === false) {
        return false
      }

      const promise = axios
        .get(this.url, { params })
        .then(res => {
          const { data } = res
          const tempData = {
            data: data.data,
            ...data.meta,
          }
          Vue.set(
            this.$data,
            'collection',
            tempData,
          )
          self.query.page = tempData.current_page

          return res
        })
        .catch(error => console.error(error))
        .finally(() => {
          this.loading = false
        })

      this.$refs.loadingComponent.load(promise)
    },
    exportToCSV() {
      const params = this.createFilter()
      if (params === false) {
        return ''
      }

      return `${this.urlExportExcel}${this.createTheParametersOfTheUrl(params)}`
    },
    storeFilter() {
      this.updateCache()

      const query = localStorage.getItem(this.replaceAll(`filter-index-${this.url}-query`, '/', ''))
      const filterCandidates = localStorage.getItem(this.replaceAll(`filter-index-${this.url}-filterCandidates`, '/', ''))
      let filter = {
        query,
        filter: filterCandidates,
        modulo_id: this.modulo_id,
        url: this.url,
      }
      this.defaultSettingsSwal()
      this.swalIcon = 'info'
      const toast = this.setupSwal()
      toast.fire({
        title: `<strong>${this.$t('filters')}</strong>`,
        text: undefined,
        input: 'text',
        inputPlaceholder: this.$t('description'),
        inputValidator: (value) => {
          if (!value) {
            return this.$t('error_alert_text')
          }
          filter = { ...filter, descripcion: value }
        },
        preConfirm: () => {
          return axios.post('/api/home/filtros', filter)
        },
      }).then(
        ({value}) => {
          if (this.checkVariable(value) && value.status === 200) {
            this.swalTitle = this.$t('update_response')
            this.swalToast = true
            this.swalTimer = 3000
            this.swalIcon = 'success'
            this.swalPosition = 'center-end'
            this.swalTimerProgressBar = true
            this.swalShowConfirmButton = false
            this.positionedDialogToast()
          }
        },
      )
    },
    availableOperators() {
      return [
        {
          title: 'igual a',
          name: 'equal_to',
          parent: ['numeric', 'string'], // 0
          component: 'single',
        },
        {
          title: 'no igual a',
          name: 'not_equal_to',
          parent: ['numeric', 'string'], // 1
          component: 'single',
        },
        {
          title: 'igual a nulo',
          name: 'equal_to_null',
          parent: ['numeric', 'string', 'select'], // 2
          component: null,
        },
        {
          title: 'menos que',
          name: 'less_than',
          parent: ['numeric'], // 3
          component: 'single',
        },
        {
          title: 'mas grande que',
          name: 'greater_than',
          parent: ['numeric'], // 4
          component: 'single',
        },
        {
          title: 'entre',
          name: 'between',
          parent: ['numeric'], // 5
          component: 'double',
        },
        {
          title: 'no entre',
          name: 'not_between',
          parent: ['numeric'], // 6
          component: 'double',
        },
        {
          title: 'contiene',
          name: 'contains',
          parent: ['string', 'select'], // 7
          component: 'single',
        },
        {
          title: 'comienza con',
          name: 'starts_with',
          parent: ['string'], // 8
          component: 'single',
        },
        {
          title: 'termina con',
          name: 'ends_with',
          parent: ['string'], // 9
          component: 'single',
        },
        {
          title: 'en el pasado',
          name: 'in_the_past',
          parent: ['datetime'], // 10
          component: 'datetime_1',
        },
        {
          title: 'en el proximo',
          name: 'in_the_next',
          parent: ['datetime'], // 11
          component: 'datetime_1',
        },
        {
          title: 'en el periodo',
          name: 'in_the_peroid',
          parent: ['datetime'], // 12
          component: 'datetime_2',
        },
        {
          title: 'igual a',
          name: 'equal_to_count',
          parent: ['counter'], // 13
          component: 'single',
        },
        {
          title: 'no igual a',
          name: 'not_equal_to_count',
          parent: ['counter'], // 14
          component: 'single',
        },
        {
          title: 'menos que',
          name: 'less_than_count',
          parent: ['counter'], // 15
          component: 'single',
        },
        {
          title: 'mas grande que',
          name: 'greater_than_count',
          parent: ['counter'], // 16
          component: 'single',
        },
        {
          title: 'igual a',
          name: 'equal_to',
          parent: ['select'], // 17
          component: 'list',
        },
        {
          title: 'no igual a',
          name: 'not_equal_to',
          parent: ['select'], // 18
          component: 'list',
        },
        {
          title: 'igual a',
          name: 'equal_to',
          parent: ['datepicker'], // 19
          component: 'datepicker_1',
        },
        {
          title: 'entre',
          name: 'between',
          parent: ['datepicker'], // 20
          component: 'datepicker_2',
        },
        {
          title: 'no entre',
          name: 'not_between',
          parent: ['datepicker'], // 21
          component: 'datepicker_2',
        },
      ]
    },
  },
  destroyed: function () {
    this.idMarked = []
    this.loading = true
    this.appliedFilters = []
    this.filterCandidates = []
    this.listInformation = []
    this.query = {
      order_column: 'created_at',
      order_direction: 'desc',
      filter_match: 'and',
      limit: 10,
      page: 1,
    }
    this.collection = {
      data: [],
    }
    this.selected = {}
  },
}
</script>
<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
<style scoped>
.multiselectOptionLabel {
  display: block;
}
</style>
<style>
.multiselect__content-wrapper {
    min-width: fit-content;
}
.encabezadoVertical {
  position: sticky;
  font-weight: bold !important;
  text-align: center;
  left: 0;
  z-index: 4;
  background-color: #c4d7ec !important;
  height: 3rem;
}
</style>
